import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Brain, ChevronRight, CheckCircle } from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card';

/**
 * Pantalla de ejemplos/instrucciones del Test Raven
 * Muestra ejemplos de matrices progresivas antes de iniciar el examen
 */
const PantallaEjemplosRaven = ({ onIniciar }) => {
  const [pasoActual, setPasoActual] = useState(0); // 0: Instrucciones, 1: Ejemplo, 2: Confirmación final
  const [opcionSeleccionada, setOpcionSeleccionada] = useState(null);

  const handleSiguiente = () => {
    if (pasoActual === 0) {
      setPasoActual(1); // Ir al ejemplo
    } else if (pasoActual === 1) {
      setPasoActual(2); // Ir a confirmación final
    } else {
      onIniciar(); // Iniciar examen real
    }
  };

  // Pantalla de confirmación final
  if (pasoActual === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full"
        >
          <Card className="border-t-4 border-t-blue-600">
            <div className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-blue-600" />
              </div>
              
              <h2 className="text-3xl font-bold text-neutral-800 mb-4">
                ¡Estás Listo para Comenzar!
              </h2>
              
              <p className="text-lg text-neutral-600 mb-6">
                Has completado la práctica. El examen real consta de <strong>12 preguntas</strong> de matrices progresivas.
              </p>

              <div className="bg-blue-50 rounded-lg p-6 border border-blue-200 mb-8">
                <div className="space-y-3 text-left">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-neutral-700">
                      <strong>Recuerda:</strong> Analiza cada matriz cuidadosamente antes de responder
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-neutral-700">
                      <strong>Sin prisa:</strong> Tómate el tiempo necesario para cada pregunta
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-neutral-700">
                      <strong>Confía en ti:</strong> Selecciona la opción que creas correcta
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border-2 border-green-300 mb-8">
                <p className="text-2xl font-bold text-green-800 mb-2">
                  ¡Mucha Suerte!
                </p>
                <p className="text-green-700">
                  Da clic en Continuar para comenzar el examen oficial
                </p>
              </div>

              <Button
                onClick={onIniciar}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-lg py-4"
              >
                <ChevronRight className="w-6 h-6 mr-2" />
                Continuar - Comenzar Examen Real
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-4xl w-full"
      >
        <Card className="border-t-4 border-t-blue-600">
          <div className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <Brain className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-neutral-800 mb-2">
                Test de Matrices Progresivas de Raven
              </h1>
              <p className="text-xl text-neutral-600">
                Ejemplos de Práctica
              </p>
            </div>

            {/* Instrucciones generales */}
            {pasoActual === 0 ? (
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <Brain className="w-5 h-5" />
                    ¿Qué es el Test Raven?
                  </h3>
                  <p className="text-blue-800 mb-4">
                    El Test de Matrices Progresivas de Raven evalúa tu capacidad de razonamiento abstracto 
                    y resolución de problemas visuales. Consiste en completar matrices identificando 
                    patrones lógicos.
                  </p>
                </div>

                <div className="bg-neutral-50 rounded-lg p-6">
                  <h3 className="font-semibold text-neutral-900 mb-3">📋 Instrucciones:</h3>
                  <ul className="space-y-2 text-neutral-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Observa cuidadosamente cada matriz y las opciones disponibles</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Identifica el patrón o lógica que sigue la secuencia</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Selecciona la pieza que completa correctamente la matriz</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>No hay límite de tiempo por pregunta, pero el examen tiene duración total</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Primero verás algunos ejemplos para familiarizarte con el formato</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>💡 Consejo:</strong> Tómate tu tiempo para analizar cada matriz. 
                    A veces el patrón puede estar en las formas, colores, posiciones o combinaciones de elementos.
                  </p>
                </div>

                <Button
                  onClick={handleSiguiente}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-lg py-3"
                >
                  Ver Ejemplos
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            ) : (
              /* Ejemplo práctico con formato real: pregunta + 8 opciones */
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                    <span className="text-blue-600 font-bold text-lg">Ejemplo</span>
                  </div>
                  <h3 className="text-2xl font-bold text-neutral-800 mb-2">
                    Pregunta de Práctica
                  </h3>
                  <p className="text-neutral-600">
                    Observa la matriz y selecciona la pieza que falta
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Pregunta (Matriz) */}
                  <div className="bg-white rounded-lg p-6 border-2 border-neutral-200 shadow-sm">
                    <p className="text-sm font-semibold text-neutral-600 mb-3 text-center">Pregunta 1</p>
                    <div className="flex justify-center items-center">
                      <img
                        src="/assets/1.PNG"
                        alt="Matriz de ejemplo"
                        className="max-w-full h-auto"
                        style={{ maxHeight: '400px' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div style={{ display: 'none' }} className="flex-col items-center justify-center text-neutral-400 p-8">
                        <Brain className="w-16 h-16 mb-2" />
                        <p className="text-sm">Matriz no disponible</p>
                      </div>
                    </div>
                  </div>

                  {/* Opciones */}
                  <div className="bg-white rounded-lg p-4 sm:p-6 border-2 border-neutral-200 shadow-sm">
                    <p className="text-sm font-semibold text-neutral-600 mb-4 text-center">Selecciona una opción:</p>
                    {/* Grid responsive: 4 columnas en desktop, 4 en móvil pero más compacto */}
                    <div className="grid grid-cols-4 gap-2 sm:gap-3">
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((numOpcion) => (
                        <button
                          key={numOpcion}
                          onClick={() => setOpcionSeleccionada(numOpcion)}
                          className={`relative aspect-square rounded-lg border-2 transition-all hover:scale-105 ${
                            opcionSeleccionada === numOpcion
                              ? 'border-blue-600 bg-blue-50 shadow-lg ring-2 ring-blue-400'
                              : 'border-neutral-200 hover:border-blue-300'
                          }`}
                        >
                          {/* Número de opción - Más pequeño y mejor posicionado */}
                          <div className={`absolute -top-1.5 -left-1.5 sm:top-0.5 sm:left-0.5 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center z-10 shadow-sm ${
                            opcionSeleccionada === numOpcion
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-neutral-300 text-neutral-600'
                          }`}>
                            <span className="text-xs font-bold">{numOpcion}</span>
                          </div>
                          
                          {/* Imagen de la opción - padding ajustado */}
                          <img
                            src={`/assets/1.${numOpcion}.PNG`}
                            alt={`Opción ${numOpcion}`}
                            className="w-full h-full object-contain p-1 sm:p-2"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          
                          {/* Checkmark si está seleccionada */}
                          {opcionSeleccionada === numOpcion && (
                            <div className="absolute inset-0 flex items-center justify-center bg-blue-600/20 rounded-lg">
                              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Feedback */}
                {opcionSeleccionada && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-50 rounded-lg p-4 border border-green-200"
                  >
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-900 mb-1">¡Bien hecho!</p>
                        <p className="text-sm text-green-800">
                          Has seleccionado la opción <strong>{opcionSeleccionada}</strong>. 
                          En el examen real, así es como debes seleccionar la pieza que completa cada matriz.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-sm text-blue-800 text-center">
                    <strong>💡 Recuerda:</strong> En el examen real tendrás 12 preguntas similares. 
                    Analiza cada matriz cuidadosamente antes de seleccionar tu respuesta.
                  </p>
                </div>

                <Button
                  onClick={handleSiguiente}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-lg py-3"
                >
                  Comenzar Examen Real
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default PantallaEjemplosRaven;
